// 週期設定（W58.1）。
//
// **三個設定，每一個都有一個明確的理由存在。**
//
// 1. **在日曆上顯示預測** —— 有人不想在日曆上看到推算的經期。
//    關掉之後**實際記錄的經期照樣顯示** —— 關掉的是「預測」，不是「事實」。
// 2. **經期長度** —— 沒有歷史記錄時，自動填結束日與推算都用這個值。
// 3. **週期長度** —— 同上。
//
// **設定是每個人的。** 推算的資料是這一對共用的，但「要不要看到預測」與
// 「我的經期通常幾天」是個人偏好 —— 共用的話，其中一個人關掉預測會把
// 另一個人的也關掉。
//
// **「− / ＋」而不是輸入框。** 兩個數字都是小範圍的整數（1–14 / 20–45），
// 而輸入框會帶來「打錯字」「打小數點」「打中文」三種問題。步進器沒有那些。

import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  updateCycleSettings,
  type CycleSettings,
} from "@/lib/api/calendar";
import { ApiError } from "@/lib/api/pair";
import { queryKeys } from "@/lib/queries/keys";
import { cn } from "@/lib/utils/cn";

export interface CycleSettingsPanelProps {
  settings: CycleSettings;
}

const PERIOD_RANGE = { min: 1, max: 14 };
const CYCLE_RANGE = { min: 20, max: 45 };

export function CycleSettingsPanel({ settings }: CycleSettingsPanelProps) {
  const queryClient = useQueryClient();

  const save = useMutation({
    mutationFn: (input: Parameters<typeof updateCycleSettings>[0]) =>
      updateCycleSettings(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["calendar-cycle"] });
      void queryClient.invalidateQueries({ queryKey: ["calendar-month"] });
      // W51 D-1 — 任何寫入都要讓首頁失效。
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });

  function bump(
    key: "period_length_days" | "cycle_length_days",
    delta: number,
    range: { min: number; max: number },
  ) {
    const next = Math.max(range.min, Math.min(range.max, settings[key] + delta));
    if (next === settings[key]) return;
    save.mutate({ [key]: next });
  }

  return (
    <section
      className="flex flex-col gap-s-3 rounded-md border border-hairline bg-paper-deep p-s-3"
      data-testid="calendar-cycle-settings"
    >
      <h2 className="text-micro uppercase tracking-wide text-ink-3">設定</h2>

      <label className="flex min-h-11 items-center gap-s-3">
        <input
          type="checkbox"
          checked={settings.predictions_enabled}
          disabled={save.isPending}
          onChange={(e) => save.mutate({ predictions_enabled: e.target.checked })}
          data-testid="calendar-cycle-settings-predictions"
          className="h-5 w-5 shrink-0 accent-ink-1"
        />
        <span className="text-body-sm text-ink-1">
          在日曆上顯示預測
          <span className="mt-s-1 block text-meta text-ink-3">
            關掉之後，實際記錄的經期照樣會顯示。
          </span>
        </span>
      </label>

      <Stepper
        label="經期長度"
        value={settings.period_length_days}
        unit="天"
        min={PERIOD_RANGE.min}
        max={PERIOD_RANGE.max}
        disabled={save.isPending}
        onDown={() => bump("period_length_days", -1, PERIOD_RANGE)}
        onUp={() => bump("period_length_days", 1, PERIOD_RANGE)}
        testIdPrefix="calendar-cycle-settings-period"
      />

      <Stepper
        label="週期長度"
        value={settings.cycle_length_days}
        unit="天"
        min={CYCLE_RANGE.min}
        max={CYCLE_RANGE.max}
        disabled={save.isPending}
        onDown={() => bump("cycle_length_days", -1, CYCLE_RANGE)}
        onUp={() => bump("cycle_length_days", 1, CYCLE_RANGE)}
        testIdPrefix="calendar-cycle-settings-cycle"
      />

      {save.isError ? (
        <p className="text-meta text-partner-strong" role="alert" data-testid="calendar-cycle-settings-error">
          {save.error instanceof ApiError ? save.error.message : "設定儲存失敗"}
        </p>
      ) : null}
    </section>
  );
}

function Stepper({
  label,
  value,
  unit,
  min,
  max,
  disabled,
  onDown,
  onUp,
  testIdPrefix,
}: {
  label: string;
  value: number;
  unit: string;
  min: number;
  max: number;
  disabled?: boolean;
  onDown: () => void;
  onUp: () => void;
  testIdPrefix: string;
}) {
  return (
    <div className="flex items-center justify-between gap-s-3">
      <span className="text-body-sm text-ink-1">{label}</span>
      <div className="flex items-center gap-s-1">
        <button
          type="button"
          onClick={onDown}
          disabled={disabled || value <= min}
          aria-label={`${label}減少一天`}
          data-testid={`${testIdPrefix}-down`}
          className={cn(
            "flex h-11 w-11 items-center justify-center rounded-pill border border-hairline-strong text-body-sm text-ink-1",
            "disabled:opacity-30",
          )}
        >
          −
        </button>
        <span
          className="min-w-[56px] text-center text-body-sm font-semibold tabular-nums text-ink-1"
          data-testid={`${testIdPrefix}-value`}
        >
          {value} {unit}
        </span>
        <button
          type="button"
          onClick={onUp}
          disabled={disabled || value >= max}
          aria-label={`${label}增加一天`}
          data-testid={`${testIdPrefix}-up`}
          className={cn(
            "flex h-11 w-11 items-center justify-center rounded-pill border border-hairline-strong text-body-sm text-ink-1",
            "disabled:opacity-30",
          )}
        >
          ＋
        </button>
      </div>
    </div>
  );
}
